import React, { Component, createRef, RefObject } from 'react';
import _isEqual from 'lodash/isEqual';
import _orderBy from 'lodash/orderBy';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { getTailwindColor, formatDollarsStatement } from '@trqk/utils';

import { unstyleScrollbar } from '../helpers';

import { EarningsLegend } from './EarningsLegend';

/**
 * @typedef {SplitsPerChartTrackProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {number} height height for the chart div
 * @property {ChartTrackPerGenreBucket} chartTrackPerGenreBucket array of ChartTrackPerGenreBucket
 * elements to render
 */
interface SplitsPerChartTrackProps {
    chartDivId: string;
    height: number;
    chartTrackPerGenreBucket: ChartTrackPerGenreBucket[];
}

interface SplitsPerChartTrackState {
    legendSources: EarningsLegendSource[];
}

const xAxesCategory = 'work_title';
const valueAxisLabelColor = getTailwindColor('tertiary-400');
const zoomLevel = 10;

export class SplitsPerChartTrack extends Component<
    SplitsPerChartTrackProps,
    SplitsPerChartTrackState
> {
    private chart: am4charts.XYChart | null = null;

    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only used as a fallback for type safety; the ref should be
    // available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: SplitsPerChartTrackProps) {
        super(props);

        this.divRef = createRef();
        this.state = {
            legendSources: [],
        };
    }

    componentDidMount(): void {
        am4core.createDeferred((): am4core.Sprite => {
            const { chartDivId, chartTrackPerGenreBucket } = this.props;

            const chart = am4core.create(
                this.divRef?.current ?? chartDivId,
                am4charts.XYChart,
            );

            // this _orderBy call should not be necessary, since we are sorting the data in
            // getTextDialFilterData, but there is some persistent bug that causes sorting to
            // disappear before the chart is rendered. This is a workaround.
            chart.data = _orderBy(
                chartTrackPerGenreBucket,
                ['splits_total_earnings'],
                ['desc'],
            );
            chart.numberFormatter.numberFormat = '$#,###.00##';
            chart.fontSize = '0.8rem';

            const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());

            categoryAxis.dataFields.category = xAxesCategory;
            categoryAxis.renderer.grid.template.disabled = true;
            // Ensures that the first category label is shown
            categoryAxis.renderer.minGridDistance = 0.01;
            categoryAxis.renderer.labels.template.fullWords = false;
            categoryAxis.renderer.labels.template.horizontalCenter = 'right';
            categoryAxis.renderer.labels.template.maxWidth = 120;
            categoryAxis.renderer.labels.template.rotation = -45;
            // eslint-disable-next-line no-template-curly-in-string
            categoryAxis.renderer.labels.template.tooltipText = '{category}';
            categoryAxis.renderer.labels.template.tooltipX =
                am4core.percent(100);
            categoryAxis.renderer.labels.template.truncate = true;
            categoryAxis.renderer.labels.template.verticalCenter = 'top';

            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

            valueAxis.min = 0;
            valueAxis.renderer.labels.template.fill =
                am4core.color(valueAxisLabelColor);

            // Set up scroll bars and zoom level per initial data array length.
            // If the array length changes, this solution will not work, as it runs only on initial
            // mount and refers to the length at that point.
            categoryAxis.maxZoomCount = zoomLevel;
            chart.zoomOutButton.disabled = true;

            if (chart.data.length <= zoomLevel) {
                categoryAxis.endLocation =
                    1 * (zoomLevel + 1 - chart.data.length);
            } else {
                chart.events.on('ready', (): void => {
                    categoryAxis.zoomToIndexes(0, zoomLevel - 1);
                });
                chart.scrollbarX = new am4core.Scrollbar();
                unstyleScrollbar(chart.scrollbarX);
            }

            this.chart = chart;
            this.addSeriesToChart();

            return chart;
        }, this);
    }

    componentDidUpdate(prevProps: SplitsPerChartTrackProps): void {
        const { chartTrackPerGenreBucket } = this.props;
        const { chart } = this;

        if (
            !_isEqual(
                prevProps.chartTrackPerGenreBucket,
                chartTrackPerGenreBucket,
            ) &&
            chart !== null
        ) {
            chart.data = chartTrackPerGenreBucket;
            this.addSeriesToChart();
        }
    }

    componentWillUnmount(): void {
        const { chart } = this;

        if (chart) {
            chart.dispose();
        }
    }

    createSeries({
        label,
        valueYKey,
    }: {
        label: string;
        valueYKey: string;
    }): am4charts.ColumnSeries | void {
        const { chart } = this;

        if (chart !== null) {
            const columnSeries = chart.series.push(
                new am4charts.ColumnSeries(),
            );

            columnSeries.name = label;
            columnSeries.dataFields.valueY = valueYKey;
            columnSeries.dataFields.categoryX = xAxesCategory;
            columnSeries.sequencedInterpolation = true;
            columnSeries.stacked = true;
            columnSeries.columns.template.width = am4core.percent(80);
            columnSeries.columns.template.tooltipText =
                // eslint-disable-next-line no-template-curly-in-string
                '[bold]{name}[/]\n[font-size:.75rem]{categoryX}: {valueY}';

            return columnSeries;
        }

        return undefined;
    }

    addSeriesToChart(): void {
        const { chart } = this;

        if (chart !== null && chart.data) {
            if (chart.series) {
                chart.series.clear();
            }

            const legendSources = [] as EarningsLegendSource[];
            const { totalMemberEarnings, totalCollaboratorEarnings } =
                chart.data.reduce(
                    (
                        acc: TrackSplitsLegendTotals,
                        chartTrackPerGenreBucket: TrackSplits,
                    ) => {
                        if (chartTrackPerGenreBucket.earnings !== null) {
                            acc.totalMemberEarnings +=
                                chartTrackPerGenreBucket.earnings;
                        }

                        if (
                            chartTrackPerGenreBucket.collaborator_earnings !==
                            null
                        ) {
                            acc.totalCollaboratorEarnings +=
                                chartTrackPerGenreBucket.collaborator_earnings;
                        }

                        return acc;
                    },
                    {
                        totalMemberEarnings: 0,
                        totalCollaboratorEarnings: 0,
                    },
                );

            const memberLabel = 'You';
            const collaboratorLabel = 'Your collaborators';

            const memberColumnSeries = this.createSeries({
                label: memberLabel,
                valueYKey: 'earnings',
            });

            const formattedMemberLegendValue =
                formatDollarsStatement(totalMemberEarnings);

            if (memberColumnSeries) {
                legendSources.push({
                    columnSeries: memberColumnSeries,
                    legendValue: formattedMemberLegendValue,
                });
            }

            const collaboratorColumnSeries = this.createSeries({
                label: collaboratorLabel,
                valueYKey: 'collaborator_earnings',
            });

            const formattedCollaboratorLegendValue = formatDollarsStatement(
                totalCollaboratorEarnings,
            );

            if (collaboratorColumnSeries) {
                legendSources.push({
                    columnSeries: collaboratorColumnSeries,
                    legendValue: formattedCollaboratorLegendValue,
                });
            }

            this.setState(() => ({ legendSources }));
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;
        const { legendSources } = this.state;

        return (
            <div>
                <div
                    id={chartDivId}
                    ref={this.divRef}
                    data-cy="splits-per-chart-track-chart"
                    className="w-full"
                    style={{ height: height - 50 }}
                />
                <EarningsLegend
                    addClasses="text-sm"
                    itemsPerRow={2}
                    legendDivId={`${chartDivId}-legend`}
                    legendSources={legendSources}
                />
            </div>
        );
    }
}
